import type { PushProviderId } from '@/config/push'

export type { PushProviderId }

export type PushPermissionStatus = 'granted' | 'denied' | 'undetermined'

/**
 * Who the user is, in terms the push provider can consume. `auth0Id` is the
 * canonical external id — Customer.io keys profiles on it.
 */
export type PushIdentity = {
  auth0Id: string
  email?: string
  name?: string
  traits?: Record<string, string | number | boolean | undefined>
}

/** A notification the user tapped, normalized across providers. */
export type PushClickEvent = {
  provider: PushProviderId
  /**
   * Raw deep link exactly as the campaign authored it — a router path, a
   * `pokerpower://` URL, or an https link. Hand it to `resolveDeepLink`.
   */
  link?: string
}

export type Unsubscribe = () => void

/**
 * The contract the push SDK is wrapped in.
 *
 * Nothing outside `src/module/push` may import `customerio-reactnative` directly —
 * that keeps the SDK swappable without touching feature code.
 */
export interface PushProvider {
  readonly id: PushProviderId

  initialize(): Promise<void>

  identify(identity: PushIdentity): Promise<void>
  clearIdentity(): Promise<void>
  /** Free-form profile/user attributes. Providers that have no equivalent skip it. */
  setAttributes?(attributes: Record<string, string>): Promise<void>
  /** The provider SDK's own anonymous ID, or null if it has none (or isn't initialized). */
  getAnonymousId?(): Promise<string | null>

  getPermissionStatus(): Promise<PushPermissionStatus>
  requestPermission?(options: { fallbackToSettings: boolean }): Promise<void>
  optIn?(): Promise<void>

  addClickListener(listener: (event: PushClickEvent) => void): Unsubscribe
  addPermissionListener?(listener: (granted: boolean) => void): Unsubscribe
}
